/**
 * Clears theme + third-party document scroll locks (GoKwik slide cart, etc.).
 * Slide cart often closes without firing checkout `modal_closed`, so we also
 * detect “locked but no modal-sized overlay” and recover.
 */
(function () {
  'use strict';

  function stripInlineScrollLock(el) {
    if (!el || !el.style) return;
    const props = [
      'overflow',
      'overflow-x',
      'overflow-y',
      'overscroll-behavior',
      'touch-action',
      'position',
      'top',
      'left',
      'right',
      'width',
      'height',
      'max-height',
    ];
    props.forEach((p) => {
      try {
        el.style.removeProperty(p);
      } catch {
        /* ignore */
      }
    });
  }

  function clearScrollJamOverrides() {
    document.querySelectorAll('[data-tatha-scroll-jam]').forEach((el) => {
      el.removeAttribute('data-tatha-scroll-jam');
      el.style.removeProperty('overflow-y');
    });
  }

  function forceScrollableChain() {
    clearScrollJamOverrides();
    const candidates = [document.documentElement, document.body, document.getElementById('MainContent')].filter(
      Boolean,
    );
    candidates.forEach((el) => {
      const oy = window.getComputedStyle(el).overflowY;
      if (oy !== 'hidden' && oy !== 'clip') return;
      el.setAttribute('data-tatha-scroll-jam', '');
      el.style.setProperty('overflow-y', 'visible', 'important');
    });
  }

  function isDocumentScrollLocked() {
    const oy = (el) => {
      if (!el) return false;
      const y = window.getComputedStyle(el).overflowY;
      return y === 'hidden' || y === 'clip';
    };
    return oy(document.documentElement) || oy(document.body);
  }

  /**
   * Large fixed/absolute layers (drawer + dimmed backdrop). When these are gone
   * but html/body are still overflow:hidden, we treat scroll as stranded.
   */
  function hasLikelyModalSizedLayer() {
    const pdpNav = document.getElementById('PdpNavLoader');
    /* During pdp-curtain-nav zoom, transform scale(0.14) shrinks getBoundingClientRect below
       the heuristics below; without this, stranded-scroll recovery hides the loader mid-animation. */
    if (pdpNav && !pdpNav.hasAttribute('hidden')) return true;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minW = Math.max(72, vw * 0.2);
    const minH = vh * 0.34;
    const all = document.body.getElementsByTagName('*');
    const cap = Math.min(all.length, 900);

    for (let i = 0; i < cap; i++) {
      const el = all[i];
      let cs;
      try {
        cs = window.getComputedStyle(el);
      } catch {
        continue;
      }
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      const op = parseFloat(cs.opacity);
      if (Number.isFinite(op) && op < 0.03) continue;

      const pos = cs.position;
      if (pos !== 'fixed' && pos !== 'absolute') continue;

      const z = parseInt(cs.zIndex, 10);
      if (!Number.isFinite(z) || z < 250) continue;

      let r;
      try {
        r = el.getBoundingClientRect();
      } catch {
        continue;
      }
      if (r.width < minW || r.height < minH) continue;
      if (r.bottom < vh * 0.12) continue;

      return true;
    }
    return false;
  }

  function tathaRecoverScrollIfStranded() {
    if (!isDocumentScrollLocked()) return;
    if (hasLikelyModalSizedLayer()) return;
    tathaUnlockDocumentScroll();
    tathaScheduleDocumentScrollUnlockInner();
  }

  /**
   * `force` bypasses the mid-animation guard below. Only bfcache restore passes it:
   * there the curtain is a leftover from the page we navigated away from, so the
   * guard would otherwise strand the page under it forever.
   */
  function tathaUnlockDocumentScroll(force) {
    const pdpNavLoader = document.getElementById('PdpNavLoader');
    if (!force && pdpNavLoader && !pdpNavLoader.hasAttribute('hidden')) return;

    document.body.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop',
    );
    document.documentElement.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop',
    );
    stripInlineScrollLock(document.documentElement);
    stripInlineScrollLock(document.body);
    stripInlineScrollLock(document.getElementById('MainContent'));

    document.documentElement.classList.remove('home-loader-active', 'pdp-nav-loader-active', 'pdp-open-curtain-active');
    document.body.classList.remove('pdp-handoff-reveal');

    const homeLoader = document.getElementById('HomeLoader');
    if (homeLoader) {
      homeLoader.classList.add('is-hidden');
      homeLoader.setAttribute('aria-hidden', 'true');
    }

    const pdpLoader = document.getElementById('PdpNavLoader');
    if (pdpLoader) {
      pdpLoader.setAttribute('hidden', '');
      pdpLoader.setAttribute('aria-hidden', 'true');
    }

    try {
      document.querySelectorAll('.pdp-open-curtain').forEach((el) => el.remove());
    } catch {
      /* ignore */
    }

    forceScrollableChain();
    requestAnimationFrame(() => forceScrollableChain());
    setTimeout(() => forceScrollableChain(), 0);

    document.documentElement.classList.add('tatha-unlock-scroll');
    try {
      window.dispatchEvent(new Event('resize'));
    } catch {
      /* ignore */
    }
    /* The override is meant to be brief — once any third-party lock has been
       beaten, drop it so it doesn't keep `position: sticky` parallax sections
       in a broken state (their sticky containing block changes when html/body
       carry forced overflow values). */
    window.setTimeout(() => {
      document.documentElement.classList.remove('tatha-unlock-scroll');
      try {
        window.dispatchEvent(new Event('scroll'));
      } catch {
        /* ignore */
      }
    }, 1500);
  }

  function tathaScheduleDocumentScrollUnlockInner() {
    [80, 200, 500, 1200, 2200, 3800].forEach((ms) => {
      window.setTimeout(() => {
        try {
          tathaUnlockDocumentScroll();
        } catch {
          /* ignore */
        }
      }, ms);
    });
  }

  function tathaScheduleDocumentScrollUnlock() {
    tathaScheduleDocumentScrollUnlockInner();
  }

  let strandedDebounce = null;
  function scheduleStrandedCheck() {
    if (strandedDebounce) clearTimeout(strandedDebounce);
    strandedDebounce = setTimeout(() => {
      strandedDebounce = null;
      tathaRecoverScrollIfStranded();
    }, 160);
  }

  let lastInteractionUnjam = 0;
  function maybeUnjamOnScrollAttempt() {
    const now = Date.now();
    if (now - lastInteractionUnjam < 450) return;
    if (!isDocumentScrollLocked()) return;
    if (hasLikelyModalSizedLayer()) return;
    lastInteractionUnjam = now;
    tathaUnlockDocumentScroll();
    tathaScheduleDocumentScrollUnlockInner();
  }

  window.tathaUnlockDocumentScroll = tathaUnlockDocumentScroll;
  window.tathaScheduleDocumentScrollUnlock = tathaScheduleDocumentScrollUnlock;
  window.tathaRecoverScrollIfStranded = tathaRecoverScrollIfStranded;

  if (document.body) {
    const mo = new MutationObserver(scheduleStrandedCheck);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  } else {
    document.addEventListener('DOMContentLoaded', function initMo() {
      document.removeEventListener('DOMContentLoaded', initMo);
      const mo = new MutationObserver(scheduleStrandedCheck);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
      mo.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    });
  }

  window.addEventListener('touchmove', maybeUnjamOnScrollAttempt, { capture: true, passive: true });
  window.addEventListener('wheel', maybeUnjamOnScrollAttempt, { capture: true, passive: true });

  window.addEventListener(
    'pageshow',
    function (e) {
      if (!e.persisted) return;
      tathaUnlockDocumentScroll(true);
      tathaScheduleDocumentScrollUnlockInner();
    },
    false,
  );
})();
